import * as React from 'react';
import { FC } from 'react';

import { isNull } from 'lodash-es';
import { useIntl } from 'react-intl';
import { FormValidationState } from 'utils';

import { FloatingLabelInput, FloatingLabelSelect } from 'components/floating-label-fields';
import { FloatingLabelTextArea } from 'components/floating-label-fields/floating-label-text-area';
import LoadingAnimation from 'components/loading-animation';
import { MobileSpacer } from 'components/mobile-spacer';
import Picture from 'components/picture';
import SanityBlockRenderer from 'components/sanity-block-renderer';

import {
  Form,
  InputLabel,
  LegalCopy,
  LoaderWrapper,
  MinimumPointsRequired,
  SendGiftButton,
  TimRewards,
  UserPoints,
  UserPointsWrapper,
  Wrapper,
} from './send-gift.styled';
import { INPUT_ID, ISendGiftViewProps } from './types';

export const SendGiftView: FC<ISendGiftViewProps> = ({
  gift,
  email,
  name,
  message,
  image,
  imageAlt,
  title,
  textContent,
  giftOptions,
  legalCopy,
  handleChange,
  userPoints,
  handleSubmit,
  errorEmailMessage,
  errorGiftMessage,
  errorNameMessage,
  isSendingEmail,
}) => {
  const { formatMessage } = useIntl();

  // Adding the first selected option (Select Gift *) as default selection
  const formatGiftOptions = [
    { value: '', label: `${formatMessage({ id: 'selectGift' })} *` },
    ...giftOptions.map(({ offerId, title: offerName, costInPoints, isRedeemable }) => ({
      value: offerId,
      label: `${offerName} | ${costInPoints}`,
      disabled: !isRedeemable, // Disable the selection if the offer is not redeemable
      testId: offerId,
    })),
  ];

  const RewardPoints = () =>
    !isNull(userPoints) ? (
      <UserPoints>{userPoints} points</UserPoints>
    ) : (
      <LoaderWrapper>
        <LoadingAnimation fillColor={Styles.color.primary} />
      </LoaderWrapper>
    );

  return (
    <Wrapper>
      {image && <Picture image={image} alt={imageAlt} objectFitContain />}

      <Form onSubmit={e => handleSubmit(e)} noValidate>
        <h1 data-testid="send-gift-title">{title}</h1>

        <SanityBlockRenderer content={textContent} />

        <TimRewards data-testid="send-gift-user-balance">
          <UserPointsWrapper>
            {formatMessage({ id: 'rewardsPointsTitle' })}
            <RewardPoints />
          </UserPointsWrapper>
        </TimRewards>

        <MinimumPointsRequired>
          {formatMessage({ id: 'minimumPointsRequired' })}
        </MinimumPointsRequired>

        <InputLabel htmlFor={INPUT_ID.GIFT}>{formatMessage({ id: 'gift' })}</InputLabel>
        <FloatingLabelSelect
          id={INPUT_ID.GIFT}
          label={gift ? formatMessage({ id: 'gift' }) : ''}
          value={gift}
          options={formatGiftOptions}
          onChange={handleChange}
          message={errorGiftMessage}
          validation={errorGiftMessage ? FormValidationState.INVALID : undefined}
          data-testid="send-gift-gift-input"
        />

        <InputLabel htmlFor={INPUT_ID.EMAIL}>{formatMessage({ id: 'to' })}</InputLabel>
        <FloatingLabelInput
          required
          id={INPUT_ID.EMAIL}
          type="email"
          value={email}
          label={formatMessage({ id: 'recipientsEmail' })}
          autocomplete="on"
          onChange={handleChange}
          validation={errorEmailMessage ? FormValidationState.INVALID : undefined}
          message={errorEmailMessage}
          data-testid="send-gift-email-input"
        />

        <InputLabel htmlFor={INPUT_ID.NAME}>{formatMessage({ id: 'from' })}</InputLabel>
        <FloatingLabelInput
          required
          id={INPUT_ID.NAME}
          type="text"
          value={name}
          label={formatMessage({ id: 'yourFullName' })}
          autocomplete="on"
          onChange={handleChange}
          validation={errorNameMessage ? FormValidationState.INVALID : undefined}
          message={errorNameMessage}
          data-testid="send-gift-name-input"
        />

        <InputLabel htmlFor={INPUT_ID.MESSAGE}>{formatMessage({ id: 'message' })}</InputLabel>
        <FloatingLabelTextArea
          id={INPUT_ID.MESSAGE}
          label={formatMessage({ id: 'personalMessageToRecipient' })}
          onChange={handleChange}
          value={message}
          validation={FormValidationState.PRISTINE}
          data-testid="send-gift-message-input"
        />

        <LegalCopy data-testid="send-gift-legal-copy">
          <SanityBlockRenderer content={legalCopy} />
        </LegalCopy>

        <SendGiftButton type="submit" data-testid="send-gift-button" isLoading={isSendingEmail}>
          {formatMessage({ id: 'sendGift' })}
        </SendGiftButton>
      </Form>

      <MobileSpacer />
    </Wrapper>
  );
};
